import React, { useState } from 'react';
import { ChevronRight, ChevronLeft, Sparkles, RotateCcw, Trophy } from 'lucide-react';
import { Button } from './ui/button';
import { rankProducts } from '../data/compareUtils';

const QUESTIONS = [
  {
    key: 'householdSize',
    label: 'How many people in your household?',
    options: [
      { value: '1-3', label: '1–3 people', sub: 'Couple / small family' },
      { value: '4-5', label: '4–5 people', sub: 'Typical Australian family' },
      { value: '6+', label: '6+ people', sub: 'Large household' },
    ],
  },
  {
    key: 'climate',
    label: "What's your local climate like?",
    options: [
      { value: 'mild', label: 'Mild', sub: 'Coastal NSW / QLD / WA' },
      { value: 'cool', label: 'Cool', sub: 'Melbourne / Adelaide / Hobart' },
      { value: 'cold', label: 'Cold', sub: 'Inland / alpine / cool zones' },
    ],
  },
  {
    key: 'priority',
    label: "What matters most to you?",
    options: [
      { value: 'efficiency', label: 'Efficiency', sub: 'Highest COP / lowest running cost' },
      { value: 'value', label: 'Best Value', sub: 'Balanced price + performance' },
      { value: 'quiet', label: 'Quietest', sub: 'Low dB for residential areas' },
      { value: 'longevity', label: 'Longevity', sub: 'Longest warranty + build' },
    ],
  },
  {
    key: 'fastRecovery',
    label: 'Do you need fast hot-water recovery?',
    options: [
      { value: 'yes', label: 'Yes, fast recovery', sub: 'Peak demand or cold zone' },
      { value: 'no', label: 'No, efficient HP is fine', sub: 'Standard hot-water use' },
    ],
  },
];

const ScorecardWizard = ({ products, onClose }) => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);

  const question = QUESTIONS[step];
  const isLast = step === QUESTIONS.length - 1;

  const handleSelect = (val) => {
    const next = { ...answers, [question.key]: val };
    setAnswers(next);
    if (isLast) {
      setDone(true);
    } else {
      setStep(step + 1);
    }
  };

  const reset = () => {
    setAnswers({});
    setStep(0);
    setDone(false);
  };

  if (done) {
    const ranked = rankProducts(products, answers);
    const top = ranked[0];

    return (
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden" data-testid="scorecard-results">
        <div className="bg-gradient-to-br from-[#3D5A47] to-[#1a2820] text-white px-6 py-5">
          <div className="flex items-center gap-2 mb-1">
            <Trophy className="w-5 h-5 text-[#B5D43B]" />
            <span className="text-xs uppercase tracking-widest text-[#B5D43B] font-bold">
              Your Best Match
            </span>
          </div>
          <h3 className="text-2xl font-bold leading-tight" data-testid="scorecard-top-name">
            {top.product.name}
          </h3>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="text-4xl font-black text-[#B5D43B]">{top.total}</span>
            <span className="text-sm text-white/60">/ 100 match score</span>
          </div>
          {top.reasons.length > 0 && (
            <ul className="mt-3 space-y-1">
              {top.reasons.map((r, i) => (
                <li key={i} className="text-sm text-white/80 flex items-start gap-2">
                  <span className="text-[#B5D43B] mt-0.5">✓</span> {r}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="p-5">
          <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold mb-3">
            Full Ranking
          </p>
          <div className="space-y-2">
            {ranked.map((r, idx) => (
              <div
                key={r.product.slug}
                className={`flex items-center gap-3 p-3 rounded-xl ${
                  idx === 0 ? 'bg-[#B5D43B]/10 border border-[#B5D43B]/30' : 'bg-gray-50'
                }`}
                data-testid={`scorecard-rank-${idx}`}
              >
                <span className="w-7 h-7 rounded-lg bg-white border border-gray-200 flex items-center justify-center font-bold text-sm text-[#3D5A47]">
                  {idx + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-[#3D5A47] truncate">{r.product.name}</p>
                  <p className="text-[11px] text-gray-500 font-mono">{r.product.model}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-2xl font-black text-[#3D5A47] leading-none">{r.total}</p>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider">match</p>
                </div>
              </div>
            ))}
          </div>
          <Button
            onClick={reset}
            variant="outline"
            className="w-full mt-4 gap-2"
            data-testid="scorecard-restart"
          >
            <RotateCcw className="w-4 h-4" /> Retake Quiz
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden" data-testid="scorecard-wizard">
      <div className="bg-gradient-to-br from-[#3D5A47] to-[#1a2820] text-white px-6 py-4">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-4 h-4 text-[#B5D43B]" />
          <span className="text-xs uppercase tracking-widest text-[#B5D43B] font-bold">
            Find Your Best Match — Step {step + 1} of {QUESTIONS.length}
          </span>
        </div>
        <div className="w-full bg-white/10 rounded-full h-1.5 mt-2">
          <div
            className="bg-[#B5D43B] h-1.5 rounded-full transition-all duration-300"
            style={{ width: `${((step + 1) / QUESTIONS.length) * 100}%` }}
          />
        </div>
      </div>

      <div className="p-6">
        <h3 className="text-xl font-bold text-[#3D5A47] mb-4">{question.label}</h3>
        <div className="space-y-2">
          {question.options.map((opt) => (
            <button
              key={opt.value}
              onClick={() => handleSelect(opt.value)}
              className="w-full text-left p-4 rounded-xl border-2 border-gray-200 hover:border-[#B5D43B] hover:bg-[#B5D43B]/5 transition-all group"
              data-testid={`scorecard-opt-${question.key}-${opt.value}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-[#3D5A47] group-hover:text-[#3D5A47]">{opt.label}</p>
                  <p className="text-xs text-gray-500">{opt.sub}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-[#B5D43B] group-hover:translate-x-1 transition-all" />
              </div>
            </button>
          ))}
        </div>
        <div className="flex justify-between mt-5">
          <Button
            variant="ghost"
            disabled={step === 0}
            onClick={() => setStep(step - 1)}
            className="gap-1 text-sm"
            data-testid="scorecard-back"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </Button>
          {onClose && (
            <Button variant="ghost" onClick={onClose} className="text-sm" data-testid="scorecard-skip">
              Skip
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScorecardWizard;
